import type { ToolsContent } from "@/lib/content/tools";
import type { ToolId } from "@/lib/content/tools/config";
import {
  asnCountryCode,
  asnNumber,
  asRecord,
  binBank,
  binCountry,
  binIsValid,
  binScheme,
  emailScoringFlags,
  ibanCentralBank,
  ibanCountry,
  ipAddress,
  ipAsnNumber,
  ipCurrency,
  ipGeo,
  ipLocationPhone,
  ipNumeric,
  ipSunInfo,
  ipTimezone,
  ipVersionLabel,
  pickBoolean,
  pickField,
  pickString,
  profanityRecommendation,
  profanityRiskLevel,
} from "@/lib/tools/api-response-fields";
import {
  calculateSuspiciousIpRiskScore,
  formatBoolean,
} from "@/lib/tools/formatters";

export type ToolResultRow = {
  label: string;
  value: string;
};

export type ToolResultSection = {
  title: string;
  rows: ToolResultRow[];
};

function boolText(
  value: boolean | undefined | null,
  ui: ToolsContent["toolsUi"],
) {
  return formatBoolean(value, ui);
}

function displayText(value: string, ui: ToolsContent["toolsUi"]) {
  if (!value || value === ui.na) {
    return ui.na;
  }
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function fmt(value: unknown, ui: ToolsContent["toolsUi"]) {
  return value !== undefined && value !== null ? String(value) : ui.na;
}

function translateApiLabel(
  value: unknown,
  labels: Record<string, string>,
  ui: ToolsContent["toolsUi"],
) {
  const text = fmt(value, ui);
  if (text === ui.na) {
    return ui.na;
  }
  return labels[text.toLowerCase()] ?? displayText(text, ui);
}

export function getToolResultSections(
  toolId: ToolId,
  data: Record<string, unknown>,
  ui: ToolsContent["toolsUi"],
): ToolResultSection[] | null {
  const results = ui.toolResults;
  const shared = results.shared;

  switch (toolId) {
    case "bin-lookup": {
      const copy = results.binLookup;
      const scheme = binScheme(data);
      const bank = binBank(data);
      const country = binCountry(data);
      const isValid = binIsValid(data);
      const L = copy.labels;

      const sections: ToolResultSection[] = [
        {
          title: copy.sections.schemeDetails,
          rows: [
            {
              label: L.valid,
              value: boolText(pickBoolean(data, "is_valid", "isValid"), ui),
            },
            ...(isValid
              ? [
                  {
                    label: L.network,
                    value: fmt(pickString(scheme, "name"), ui),
                  },
                  {
                    label: L.luhnCheck,
                    value: boolText(
                      pickBoolean(scheme, "is_luhn", "isLuhn"),
                      ui,
                    ),
                  },
                  {
                    label: L.prepaid,
                    value: boolText(
                      pickBoolean(scheme, "is_prepaid", "isPrepaid"),
                      ui,
                    ),
                  },
                  {
                    label: L.cardType,
                    value: displayText(fmt(pickString(scheme, "type"), ui), ui),
                  },
                  {
                    label: L.brand,
                    value: fmt(pickString(scheme, "brand", "coName"), ui),
                  },
                  {
                    label: L.currency,
                    value: fmt(pickString(scheme, "currency"), ui),
                  },
                ]
              : []),
          ],
        },
      ];

      if (isValid) {
        sections.push(
          {
            title: copy.sections.issuingBank,
            rows: [
              { label: L.bankId, value: fmt(bank?.id, ui) },
              { label: L.bankName, value: fmt(bank?.name, ui) },
              { label: L.city, value: fmt(bank?.city, ui) },
              { label: L.latitude, value: fmt(bank?.latitude, ui) },
              { label: L.longitude, value: fmt(bank?.longitude, ui) },
            ],
          },
          {
            title: copy.sections.issuingCountry,
            rows: [
              { label: L.countryCode, value: fmt(country?.alpha2, ui) },
              { label: L.country, value: fmt(country?.name, ui) },
              { label: L.dialingCode, value: fmt(country?.code, ui) },
              { label: L.numericCode, value: fmt(country?.numeric, ui) },
              { label: L.flag, value: fmt(country?.emoji, ui) },
              { label: L.continent, value: fmt(country?.continent, ui) },
              {
                label: L.language,
                value: fmt(
                  pickString(country, "language_code", "languageCode"),
                  ui,
                ),
              },
              {
                label: L.nativeLanguage,
                value: fmt(
                  pickString(country, "language_native", "languageNative"),
                  ui,
                ),
              },
            ],
          },
        );
      }

      return sections;
    }

    case "iban-validation": {
      const copy = results.ibanValidation;
      const L = copy.labels;
      const country = ibanCountry(data);
      const centralBank = ibanCentralBank(country);
      return [
        {
          title: copy.sections.ibanData,
          rows: [
            {
              label: L.validIban,
              value: boolText(pickBoolean(data, "is_valid", "isValid"), ui),
            },
            {
              label: L.iban,
              value: (data.formats as { human?: string })?.human ?? ui.na,
            },
            { label: L.country, value: fmt(pickString(country, "name"), ui) },
            {
              label: L.iso3166,
              value: fmt(pickString(country, "iso3166", "ISO3166"), ui),
            },
            {
              label: L.currency,
              value: fmt(pickString(country, "currency"), ui),
            },
            {
              label: L.centralBankName,
              value: fmt(pickString(centralBank, "name"), ui),
            },
            {
              label: L.centralBankUrl,
              value: fmt(pickString(centralBank, "url"), ui),
            },
            {
              label: L.membership,
              value: fmt(pickString(country, "membership"), ui),
            },
            {
              label: L.euMember,
              value: boolText(pickBoolean(country, "is_eu", "isEU"), ui),
            },
            {
              label: L.ibanLength,
              value: fmt(pickString(country, "length"), ui),
            },
            {
              label: L.sepaMember,
              value: boolText(pickBoolean(country, "is_sepa", "isSEPA"), ui),
            },
            {
              label: L.swiftIssued,
              value: boolText(
                pickBoolean(country, "swift_official", "swiftOfficial"),
                ui,
              ),
            },
          ],
        },
      ];
    }

    case "profanity-detection": {
      const copy = results.profanityDetection;
      return [
        {
          title: copy.sections.result,
          rows: [
            {
              label: copy.labels.riskLevel,
              value: translateApiLabel(
                profanityRiskLevel(data),
                shared.riskLevels,
                ui,
              ),
            },
            {
              label: copy.labels.recommendation,
              value: translateApiLabel(
                profanityRecommendation(data),
                shared.recommendations,
                ui,
              ),
            },
          ],
        },
      ];
    }

    case "disposable-email": {
      const copy = results.disposableEmail;
      const L = copy.labels;
      const flags = emailScoringFlags(data);
      return [
        {
          title: copy.sections.scoringDetails,
          rows: [
            { label: L.validEmail, value: boolText(flags.is_valid, ui) },
            { label: L.freeEmail, value: boolText(flags.is_free, ui) },
            { label: L.disposable, value: boolText(flags.is_disposable, ui) },
            { label: L.roleBased, value: boolText(flags.is_role_based, ui) },
            {
              label: L.educational,
              value: boolText(flags.is_educational, ui),
            },
            {
              label: L.newDomain,
              value: boolText(flags.domain_is_new, ui),
            },
          ],
        },
      ];
    }

    case "domain-scoring": {
      const copy = results.domainScoring;
      const L = copy.labels;
      return [
        {
          title: copy.sections.scoringDetails,
          rows: [
            {
              label: L.dangerous,
              value: boolText(data.is_dangerous as boolean, ui),
            },
            {
              label: L.disposable,
              value: boolText(data.is_disposable as boolean, ui),
            },
            {
              label: L.newDomain,
              value: boolText(data.is_new as boolean, ui),
            },
            {
              label: L.spfRecord,
              value: boolText(data.is_spf as boolean, ui),
            },
            {
              label: L.dmarcRecord,
              value: boolText(data.is_dmarc as boolean, ui),
            },
            {
              label: L.dkimRecord,
              value: boolText(data.is_dkim as boolean, ui),
            },
            {
              label: L.mxRecord,
              value: boolText(data.is_mx as boolean, ui),
            },
            {
              label: L.bimiRecord,
              value: boolText(data.is_bimi as boolean, ui),
            },
            {
              label: L.createDate,
              value: fmt(data.created_at, ui),
            },
          ],
        },
      ];
    }

    case "asn-lookup": {
      const copy = results.asnLookup;
      const L = copy.labels;
      return [
        {
          title: copy.sections.asNumberData,
          rows: [
            { label: L.asNumber, value: fmt(asnNumber(data), ui) },
            { label: L.name, value: fmt(data.name, ui) },
            { label: L.country, value: fmt(asnCountryCode(data), ui) },
            { label: L.organization, value: fmt(data.org, ui) },
            { label: L.phone, value: fmt(data.phone, ui) },
            { label: L.email, value: fmt(data.email, ui) },
            { label: L.domain, value: fmt(data.domain, ui) },
            { label: L.status, value: fmt(data.status, ui) },
            { label: L.created, value: fmt(data.created, ui) },
            {
              label: L.type,
              value: fmt(data.type, ui).replace(/^./, (c) => c.toUpperCase()),
            },
            { label: L.registry, value: fmt(data.registry, ui) },
          ],
        },
      ];
    }

    case "phone-validation": {
      const copy = results.phoneValidation;
      const L = copy.labels;
      const carrier = pickString(data, "carrier");

      const rows: ToolResultRow[] = [
        {
          label: L.validNumber,
          value: boolText(pickBoolean(data, "is_valid", "isValid"), ui),
        },
        {
          label: L.phone,
          value: fmt(pickString(data, "phone"), ui),
        },
        {
          label: L.countryCode,
          value: fmt(pickString(data, "country_code", "countryCode"), ui),
        },
        {
          label: L.riskLevel,
          value: translateApiLabel(
            pickString(data, "risk_level", "riskLevel"),
            shared.riskLevels,
            ui,
          ),
        },
        {
          label: L.recommendation,
          value: translateApiLabel(
            pickString(data, "recommendation"),
            shared.recommendations,
            ui,
          ),
        },
        {
          label: L.disposableOrSpam,
          value: boolText(
            pickBoolean(data, "is_disposable", "isDisposable"),
            ui,
          ),
        },
        {
          label: L.blacklisted,
          value: boolText(
            pickBoolean(data, "is_blacklisted", "isBlacklisted"),
            ui,
          ),
        },
      ];

      if (carrier) {
        rows.splice(3, 0, { label: L.carrier, value: carrier });
      }

      return [
        {
          title: copy.sections.validationResults,
          rows,
        },
      ];
    }

    case "proxy-vpn-check": {
      const security = asRecord(data.security);
      const copy = ui.proxyVpnCheck;

      const score = pickField(data, "score");
      const scoreDisplay =
        typeof score === "number" ? `${score}%` : fmt(score, ui);

      const sections: ToolResultSection[] = [
        {
          title: copy.sections.threatScore,
          rows: [
            { label: copy.labels.ipAddress, value: fmt(ipAddress(data), ui) },
            { label: copy.labels.threatScore, value: scoreDisplay },
            {
              label: copy.labels.riskLevel,
              value: translateApiLabel(
                pickString(data, "risk_level", "riskLevel"),
                copy.riskLevels,
                ui,
              ),
            },
            {
              label: copy.labels.recommendation,
              value: translateApiLabel(
                pickString(data, "recommendation"),
                copy.recommendations,
                ui,
              ),
            },
          ],
        },
        {
          title: copy.sections.securitySignals,
          rows: [
            {
              label: copy.labels.vpnOrProxy,
              value: boolText(
                pickBoolean(security, "vpn_or_proxy", "isProxy"),
                ui,
              ),
            },
            {
              label: copy.labels.vpnOrProxyType,
              value: fmt(
                pickString(security, "vpn_or_proxy_type", "proxyType"),
                ui,
              ),
            },
            {
              label: copy.labels.tor,
              value: boolText(pickBoolean(security, "tor", "isTor"), ui),
            },
            {
              label: copy.labels.bot,
              value: boolText(pickBoolean(security, "bot", "isBot"), ui),
            },
            {
              label: copy.labels.relay,
              value: boolText(pickBoolean(security, "relay", "isRelay"), ui),
            },
            {
              label: copy.labels.relayCountry,
              value: fmt(pickString(security, "relay_country"), ui),
            },
            {
              label: copy.labels.relayCity,
              value: fmt(pickString(security, "relay_city"), ui),
            },
            {
              label: copy.labels.hosting,
              value: boolText(
                pickBoolean(security, "hosting", "isHosting"),
                ui,
              ),
            },
            {
              label: copy.labels.blacklisted,
              value: boolText(
                pickBoolean(security, "is_blacklisted", "blacklisted"),
                ui,
              ),
            },
          ],
        },
      ];

      return sections;
    }

    case "ip-lookup": {
      const copy = results.ipLookup;
      const L = copy.labels;
      const location = asRecord(data.location);
      const flag = asRecord(location?.flag);
      const asn = asRecord(data.asn);
      const security = asRecord(data.security);
      const sunInfo = ipSunInfo(data);
      const riskScore = calculateSuspiciousIpRiskScore(security);

      return [
        {
          title: copy.sections.ipData,
          rows: [
            { label: L.ipAddress, value: fmt(ipAddress(data), ui) },
            { label: L.ipNumberDecimal, value: fmt(ipNumeric(data), ui) },
            { label: L.type, value: ipVersionLabel(data, ui.na) },
            { label: L.asNumber, value: fmt(ipAsnNumber(data), ui) },
            { label: L.asnName, value: fmt(asn?.name, ui) },
            { label: L.asnOrganization, value: fmt(asn?.org, ui) },
            { label: L.asnDomain, value: fmt(asn?.domain, ui) },
            { label: L.asnType, value: fmt(asn?.type, ui) },
            { label: L.riskScore, value: `${riskScore}%` },
          ],
        },
        {
          title: copy.sections.locationData,
          rows: [
            {
              label: L.country,
              value: `${flag?.emoji ?? ""} ${fmt(ipGeo(data, "country", "countryName"), ui)}`,
            },
            {
              label: L.countryCodeAlpha2,
              value: fmt(ipGeo(data, "country_code", "countryCode"), ui),
            },
            {
              label: L.countryGeoNameId,
              value: fmt(
                ipGeo(data, "country_geo_name_id", "countryGeoNameID"),
                ui,
              ),
            },
            {
              label: L.continent,
              value: fmt(ipGeo(data, "continent", "continentName"), ui),
            },
            {
              label: L.continentCodeAlpha2,
              value: fmt(ipGeo(data, "continent_code", "continentCode"), ui),
            },
            {
              label: L.countryPhoneCode,
              value: fmt(ipLocationPhone(data), ui),
            },
            {
              label: L.countryPopulation,
              value: fmt(location?.population, ui),
            },
            {
              label: L.continentGeoNameId,
              value: fmt(
                ipGeo(data, "continent_geo_name_id", "continentGeoNameID"),
                ui,
              ),
            },
            {
              label: L.stateRegion,
              value: fmt(ipGeo(data, "region", "regionName"), ui),
            },
            { label: L.city, value: fmt(ipGeo(data, "city", "cityName"), ui) },
            { label: L.zip, value: fmt(ipGeo(data, "postal", "zipCode"), ui) },
            {
              label: L.latitude,
              value: fmt(ipGeo(data, "latitude", "latitude"), ui),
            },
            {
              label: L.longitude,
              value: fmt(ipGeo(data, "longitude", "longitude"), ui),
            },
            {
              label: L.currencyName,
              value: fmt(ipCurrency(data, "name", "currencyName"), ui),
            },
            {
              label: L.currencyCode,
              value: fmt(ipCurrency(data, "code", "currencyCode"), ui),
            },
            {
              label: L.currencySymbol,
              value: fmt(ipCurrency(data, "symbol", "currencySymbol"), ui),
            },
          ],
        },
        {
          title: copy.sections.timezoneData,
          rows: [
            {
              label: L.timezoneName,
              value: fmt(ipTimezone(data, "name"), ui),
            },
            {
              label: L.timezoneAbbreviation,
              value: fmt(ipTimezone(data, "abbreviation"), ui),
            },
            {
              label: L.timezoneOffset,
              value: fmt(ipTimezone(data, "offset"), ui),
            },
            {
              label: L.localTime,
              value: fmt(ipTimezone(data, "current_time", "currentTime"), ui),
            },
            {
              label: L.localTimeUnix,
              value: fmt(
                ipTimezone(data, "current_timestamp", "currentTimestamp"),
                ui,
              ),
            },
            { label: L.sunset, value: fmt(sunInfo?.sunset, ui) },
            { label: L.sunrise, value: fmt(sunInfo?.sunrise, ui) },
            { label: L.transit, value: fmt(sunInfo?.transit, ui) },
            {
              label: L.dayLength,
              value: fmt(pickField(sunInfo, "day_length", "dayLength"), ui),
            },
          ],
        },
      ];
    }

    default:
      return null;
  }
}
